import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/layout/Layout';
import { toast } from 'sonner';
import { getImageUrl } from '../utils/apiUrl';
import { EventHero, EventInfo, TicketCard, TicketPurchaseModal } from '../components/events';
import { Button } from '../components/ui/button';
import { Copy, Link2, Check } from 'lucide-react';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// African countries that use Paystack
const PAYSTACK_COUNTRIES = [
  'Nigeria', 'Ghana', 'Kenya', 'South Africa', 'Egypt', 'Tanzania', 'Rwanda', 'Cameroon', 'Senegal', 'Ivory Coast',
  'nigeria', 'ghana', 'kenya', 'south africa', 'egypt', 'tanzania', 'rwanda', 'cameroon', 'senegal', 'ivory coast',
  'NG', 'GH', 'KE', 'ZA', 'EG', 'TZ', 'RW', 'CM', 'SN', 'CI'
];

// Currency symbols lookup
const CURRENCY_SYMBOLS = {
  'NGN': '₦', 'GHS': 'GH₵', 'KES': 'KSh', 'ZAR': 'R',
  'CAD': 'CA$', 'USD': '$', 'GBP': '£', 'EUR': '€'
};

// Determine payment gateway based on EVENT's country (not user location)
const getPaymentGatewayForEvent = (eventCountry) => {
  if (PAYSTACK_COUNTRIES.includes(eventCountry)) {
    return { gateway: 'paystack', country: eventCountry, region: 'Africa' };
  }
  // Default to Stripe for North America / Europe / International
  return { gateway: 'stripe', country: eventCountry, region: 'International' };
};

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1563841930606-67e2bce48b78?auto=format&fit=crop&q=80&w=1600';

export default function EventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  
  // Referral code from URL
  const refCodeFromUrl = searchParams.get('ref') || '';
  
  // Core state
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [quantity, setQuantity] = useState('1');
  const [showTicketModal, setShowTicketModal] = useState(false);
  
  // Share referral state
  const [myRefCode, setMyRefCode] = useState('');
  const [generatingRef, setGeneratingRef] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isAffiliate, setIsAffiliate] = useState(false);
  const [commissionPercent, setCommissionPercent] = useState(0);
  
  // Image state
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  
  // Payment gateway state - default to Paystack for West Africa
  const [paymentGateway, setPaymentGateway] = useState({ gateway: 'paystack', country: 'Nigeria', region: 'West Africa' });

  // Fetch event data
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/events/${eventId}`, {
          headers: { 'Cache-Control': 'no-cache' }
        });
        setEvent(response.data);
        
        // Set payment gateway based on event's country
        const eventCountry = response.data.country || 'Nigeria';
        setPaymentGateway(getPaymentGatewayForEvent(eventCountry));
        
        const rawImageUrl = response.data.image_url;
        
        if (!rawImageUrl) {
          setImageSrc(FALLBACK_IMAGE);
          setImageLoaded(true);
          return;
        }
        
        const imgUrl = getImageUrl(rawImageUrl);
        
        if (rawImageUrl.startsWith('http://') || rawImageUrl.startsWith('https://')) {
          setImageSrc(imgUrl);
          setImageLoaded(true);
        } else {
          const img = new Image();
          img.onload = () => {
            setImageSrc(imgUrl);
            setImageLoaded(true);
          };
          img.onerror = () => {
            setImageSrc(FALLBACK_IMAGE);
            setImageLoaded(true);
          };
          img.src = imgUrl;
        }
        
        if (response.data.ticket_types?.length > 0) {
          setSelectedTicket(response.data.ticket_types[0]);
        }
      } catch (error) {
        console.error('Error fetching event:', error);
        toast.error('Event not found');
        navigate('/events');
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [eventId, navigate]);

  // Track referral click and capture ref_code server-side
  useEffect(() => {
    if (refCodeFromUrl && eventId) {
      axios.post(`${API_URL}/api/referrals/track-click`, { ref_code: refCodeFromUrl }).catch(() => {});
      axios.post(`${API_URL}/api/referrals/capture`, { event_id: eventId, ref_code: refCodeFromUrl }, { withCredentials: true }).catch(() => {});
    }
  }, [refCodeFromUrl, eventId]);

  // Check if user is an approved affiliate for this event
  useEffect(() => {
    if (isAuthenticated && eventId) {
      axios.get(`${API_URL}/api/referrals/check-affiliate/${eventId}`)
        .then(res => {
          setIsAffiliate(res.data.is_affiliate);
          setCommissionPercent(res.data.commission_percent || 0);
        })
        .catch(() => {});
    }
  }, [isAuthenticated, eventId]);

  // Generate or get referral link for sharing
  const handleShareReferral = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to generate a referral link');
      return;
    }
    setGeneratingRef(true);
    try {
      const res = await axios.post(`${API_URL}/api/referrals/create`, { event_id: eventId });
      const code = res.data.referral.ref_code;
      setMyRefCode(code);
      const url = `${window.location.origin}/events/${eventId}?ref=${code}`;
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success('Referral link copied to clipboard!');
        setTimeout(() => setCopied(false), 3000);
      } catch {
        toast.success('Referral link generated!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate referral link');
    } finally {
      setGeneratingRef(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 w-32 bg-muted rounded mb-4" />
            <div className="aspect-[21/9] bg-muted rounded-2xl mb-8" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-4">
                <div className="h-10 bg-muted rounded w-3/4" />
                <div className="h-6 bg-muted rounded w-1/2" />
                <div className="h-32 bg-muted rounded" />
              </div>
              <div className="h-64 bg-muted rounded-2xl" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!event) return null;

  // Computed values
  const lowestPrice = event.ticket_types?.length > 0 
    ? Math.min(...event.ticket_types.map(t => t.price)) 
    : 0;
  const hasFreeTIckets = event.ticket_types?.some(t => t.is_free);
  // If any ticket type has null available (unlimited), the event is not sold out
  const hasUnlimitedTickets = event.ticket_types?.some(t => t.available === null || t.available === undefined);
  const totalAvailable = hasUnlimitedTickets ? Infinity : (event.ticket_types?.reduce((sum, t) => sum + (t.available || 0), 0) || 0);
  
  const isReservationEvent = event.event_type === 'reservation';
  const isFreeEvent = event.event_type === 'free';
  const isRecurringEvent = event.is_recurring;
  const hasReservationLimit = isReservationEvent && event.max_reservations && event.max_reservations > 0;

  // Currency symbol for this event
  const cs = CURRENCY_SYMBOLS[event.currency] || '₦';

  // Get price display based on event type
  const getPriceDisplay = (ticket = null) => {
    if (isRecurringEvent) {
      if (event.price_range && event.price_range.min_price > 0) {
        return <span>From {cs}{event.price_range.min_price.toLocaleString()}</span>;
      } else if (event.price_range && event.price_range.max_price > 0) {
        return <span>Up to {cs}{event.price_range.max_price.toLocaleString()}</span>;
      }
      return <span className="text-muted-foreground">See venue for pricing</span>;
    }
    
    if (isFreeEvent) {
      return <span className="text-secondary">Free</span>;
    } else if (isReservationEvent) {
      return <span className="text-blue-400">RSVP Required</span>;
    } else if (ticket?.is_free || hasFreeTIckets) {
      return <span className="text-secondary">Free</span>;
    } else {
      return `${cs}${(ticket?.price || lowestPrice).toLocaleString()}`;
    }
  };
  
  // Get button text based on event type
  const getButtonText = () => {
    if (totalAvailable === 0 && !isRecurringEvent) return 'Sold Out';
    if (isRecurringEvent && event.requires_rsvp) return 'RSVP Now';
    if (isRecurringEvent) return 'Visit Venue';
    if (isReservationEvent) return 'Reserve Spot';
    if (isFreeEvent) return 'Get Free Ticket';
    return 'Get Tickets';
  };

  return (
    <Layout showFooter={false}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Hero Section */}
        <EventHero 
          event={event} 
          imageSrc={imageSrc} 
          imageLoaded={imageLoaded} 
        />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Event Info */}
          <EventInfo event={event} />

          {/* Ticket Card + Share Section */}
          <div className="space-y-4">
            <TicketCard
              event={event}
              selectedTicket={selectedTicket}
              setSelectedTicket={setSelectedTicket}
              onGetTickets={() => setShowTicketModal(true)}
              isRecurringEvent={isRecurringEvent}
              isFreeEvent={isFreeEvent}
              isReservationEvent={isReservationEvent}
              hasReservationLimit={hasReservationLimit}
              totalAvailable={totalAvailable}
              lowestPrice={lowestPrice}
              hasFreeTIckets={hasFreeTIckets}
              getPriceDisplay={getPriceDisplay}
              getButtonText={getButtonText}
            />

            {/* Share & Earn Referral - Only for approved affiliates or event owner */}
            {isAuthenticated && isAffiliate && (
              <div className="p-4 border rounded-2xl bg-card" data-testid="share-referral-section">
                <div className="flex items-center gap-2 mb-2">
                  <Link2 className="w-4 h-4 text-primary" />
                  <span className="text-sm font-semibold">Share & Earn</span>
                  {commissionPercent > 0 && (
                    <span className="text-xs bg-green-500/10 text-green-600 px-2 py-0.5 rounded-full font-medium">
                      {commissionPercent}% commission
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Generate a referral link to share this event. Track your clicks and conversions.
                </p>
                {myRefCode ? (
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-muted px-3 py-2 rounded-lg truncate" data-testid="my-ref-code">
                      {`${window.location.origin}/events/${eventId}?ref=${myRefCode}`}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        try { navigator.clipboard.writeText(`${window.location.origin}/events/${eventId}?ref=${myRefCode}`); setCopied(true); toast.success('Copied!'); setTimeout(() => setCopied(false), 3000); } catch { toast.info('Select and copy the link manually'); }
                      }}
                      data-testid="copy-referral-btn"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={handleShareReferral}
                    disabled={generatingRef}
                    data-testid="generate-referral-btn"
                  >
                    {generatingRef ? 'Generating...' : 'Get Referral Link'}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Ticket Purchase Modal */}
        <TicketPurchaseModal
          open={showTicketModal}
          onOpenChange={setShowTicketModal}
          event={event}
          selectedTicket={selectedTicket}
          setSelectedTicket={setSelectedTicket}
          quantity={quantity}
          setQuantity={setQuantity}
          isAuthenticated={isAuthenticated}
          user={user}
          paymentGateway={paymentGateway}
          isFreeEvent={isFreeEvent}
          isReservationEvent={isReservationEvent}
          hasReservationLimit={hasReservationLimit}
          totalAvailable={totalAvailable}
        />
      </div>
    </Layout>
  );
}
